/**
 * LiensPage — the lab↔médecin link table: invite → approve (seam-2 decision) → revoke. The roster is
 * sourced from GET /referral/analytics (each card carries the médecin + its current lien_statut +
 * retours_count) — there is no separate GET-liens endpoint by design, so the analytics read is the join
 * root for the link table. Drives the full 5-state DoD + the CLOUD-only "indisponible" state via
 * QueryBoundary.
 *
 * Actions, each gated on referral.lien.manage (and disabled when the action is not applicable to the
 * row's status):
 *   • Inviter — opens an `invite` lien (returns the seam-2 callback token).
 *   • Décision — applies the seam-2 decision from the callback token (invite → approuve | rejete).
 *   • Révoquer — archives an `approuve` lien + cascades its codes → inactif.
 *
 * Contract note: the analytics read exposes lien_statut but NOT lien_id, and revoke is keyed by lien_id.
 * We capture the lien_id returned by an invite in a session-local map so a freshly-invited médecin can be
 * revoked; rows whose lien_id the read never surfaced keep the action disabled (we never invent an id).
 */
import { useMemo, useState } from "react";
import { Button, Card, Table, type Column } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { REF_CAPS } from "../caps";
import { useAnalytics, useLabId } from "../api";
import type { MedecinCard } from "../types";
import { isLinkLive } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { LienBadge } from "../components/StatusBadge";
import { QueryBoundary } from "../components/QueryBoundary";
import { InviteMedecinModal } from "../components/InviteMedecinModal";
import { DecisionModal } from "../components/DecisionModal";
import { RevokeModal } from "../components/RevokeModal";

export function LiensPage() {
  const { t } = useT();
  const labId = useLabId();
  const canRead = useCan(REF_CAPS.analyticsRead);
  const canManage = useCan(REF_CAPS.lienManage);

  const query = useAnalytics(labId, canRead);
  const cards = useMemo(() => query.data?.cards ?? [], [query.data]);

  // session-local médecin_id → lien_id (from invite results — the read does not carry lien_id).
  const [lienIds, setLienIds] = useState<Record<string, string>>({});

  const [inviteOpen, setInviteOpen] = useState(false);
  const [decisionFor, setDecisionFor] = useState<MedecinCard | null>(null);
  const [revokeFor, setRevokeFor] = useState<MedecinCard | null>(null);

  const columns: ReadonlyArray<Column<MedecinCard>> = [
    {
      key: "nom",
      header: t("referral.liens.col.nom"),
      sortable: true,
      sortAccessor: (r) => r.nom,
      cell: (r) => r.nom,
    },
    {
      key: "email",
      header: t("referral.liens.col.email"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.novera_email}</span>,
    },
    {
      key: "statut",
      header: t("referral.liens.col.statut"),
      cell: (r) => <LienBadge statut={r.lien_statut} />,
    },
    {
      key: "retours",
      header: t("referral.liens.col.retours"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.retours_count,
      cell: (r) => <span className="nv-ltr-island">{r.retours_count}</span>,
    },
    {
      key: "actions",
      header: t("referral.liens.col.actions"),
      align: "end",
      cell: (r) => {
        const lienId = lienIds[r.medecin_id];
        const canRevoke = isLinkLive(r.lien_statut) && !!lienId;
        return (
          <div
            style={{
              display: "inline-flex",
              gap: "var(--nv-s-2)",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              size="sm"
              variant="secondary"
              disabled={!canManage || r.lien_statut !== "invite"}
              onClick={() => setDecisionFor(r)}
            >
              {t("referral.liens.action.decision")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!canManage || !canRevoke}
              onClick={() => setRevokeFor(r)}
            >
              {t("referral.liens.action.revoke")}
            </Button>
          </div>
        );
      },
    },
  ];

  const header = (
    <PageHeader
      title={t("referral.liens.title")}
      subtitle={t("referral.liens.subtitle")}
      actions={
        <Button disabled={!canManage} onClick={() => setInviteOpen(true)}>
          {t("referral.liens.invite")}
        </Button>
      }
    />
  );

  return (
    <>
      <QueryBoundary
        forbidden={!canRead}
        forbiddenCapabilities={[REF_CAPS.analyticsRead]}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error ?? null}
        hasData={cards.length > 0}
        onRetry={() => void query.refetch()}
        header={header}
      >
        {header}
        <Card padded={false}>
          <div style={{ padding: "var(--nv-s-3)" }}>
            <Table
              columns={columns}
              rows={cards}
              rowKey={(r) => r.medecin_id}
              caption={t("referral.liens.title")}
              pageSize={12}
              emptyTitle={t("referral.liens.empty.title")}
              emptyDescription={t("referral.liens.empty.desc")}
              emptyAction={
                canManage ? (
                  <Button onClick={() => setInviteOpen(true)}>{t("referral.liens.invite")}</Button>
                ) : undefined
              }
            />
          </div>
        </Card>
      </QueryBoundary>

      <InviteMedecinModal
        open={inviteOpen}
        labId={labId}
        disabled={!canManage}
        onClose={() => setInviteOpen(false)}
        onInvited={(res) => {
          setLienIds((m) => ({ ...m, [String(res.medecin_id)]: String(res.lien_id) }));
          void query.refetch();
        }}
      />

      <DecisionModal
        open={!!decisionFor}
        labId={labId}
        disabled={!canManage}
        medecinNom={decisionFor?.nom}
        onClose={() => setDecisionFor(null)}
        onDecided={() => void query.refetch()}
      />

      <RevokeModal
        open={!!revokeFor}
        labId={labId}
        lienId={revokeFor ? (lienIds[revokeFor.medecin_id] ?? null) : null}
        medecinNom={revokeFor?.nom}
        disabled={!canManage}
        onClose={() => setRevokeFor(null)}
        onRevoked={() => {
          if (revokeFor) {
            setLienIds((m) => {
              const next = { ...m };
              delete next[revokeFor.medecin_id];
              return next;
            });
          }
          void query.refetch();
        }}
      />
    </>
  );
}
